'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Arterial Plaque: The 2024 NEJM Heart Study"
      author="MicroPlastics Editorial Team"
      date="April 17, 2026"
      readTime="10 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="Microplastics found in carotid artery plaque. NEJM 2024 study"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        In March 2024 a team led by Raffaele Marfella at the University of Campania
        published a landmark study in the <em>New England Journal of Medicine</em>.
        They examined carotid-artery plaque removed from 257 patients during surgery
        and found polyethylene microplastics in <strong>58%</strong> and PVC in
        ~12%. Over the 34-month follow-up, patients with plastics in their plaque
        had a <strong>4.53× higher risk</strong> of heart attack, stroke, or death
        from any cause. This is the first prospective human evidence linking
        microplastic exposure to cardiovascular events.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>The Marfella 2024 study is the first prospective human trial connecting microplastic presence in tissue with measurable cardiovascular harm.</>,
          <>Polyethylene (PE) and polyvinyl chloride (PVC) were the dominant polymers found in plaque, the same polymers used in water pipes, packaging, and disposables.</>,
          <>The hazard ratio of <strong>4.53</strong> was statistically robust and held after adjusting for age, sex, BMI, smoking, hypertension, diabetes, and statin use.</>,
          <>Tissue near plastic particles showed elevated inflammatory markers (IL-6, IL-18, TNF-α), a plausible biological mechanism for the harm signal.</>,
          <>The trial does not prove causation, but it changes the burden of proof: the &ldquo;harmless until proven otherwise&rdquo; default is no longer scientifically defensible.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What the study actually did
      </h2>
      <p>
        Marfella and colleagues enrolled 304 patients undergoing carotid
        endarterectomy, a routine surgery to remove fatty plaque from neck arteries.
        Of these, 257 completed the 34-month follow-up. The team analysed the
        excised plaque using pyrolysis–gas chromatography–mass spectrometry, the
        same technique used in the 2022 Leslie blood study, plus electron microscopy
        to visualise individual particles.
      </p>
      <p>
        They then tracked which patients suffered a major adverse cardiovascular event (non-fatal heart attack, non-fatal stroke, or all-cause death) over the
        following ~3 years.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The headline number: 4.53× higher risk
      </h2>
      <p>
        Of the 150 patients whose plaque contained polyethylene or PVC, 30 (20%) had
        a heart attack, stroke, or died. Of the 107 patients with no detectable
        plastic, only 8 (7.5%) did.
      </p>
      <p>
        After adjusting for the standard cardiovascular risk factors (age, sex,
        smoking, BMI, hypertension, diabetes, statin therapy, prior cardiovascular
        disease), the hazard ratio for the plastic-positive group was
        <strong> 4.53 (95% confidence interval 2.00–10.27)</strong>. That confidence
        interval does not cross 1, meaning the result is statistically significant, and the effect size dwarfs many traditional risk factors.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why this study mattered scientifically
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>It is prospective, not retrospective.</strong> The plaque samples
          were taken first and the patients were followed forward in time, the
          gold standard for causal-inference epidemiology.
        </li>
        <li>
          <strong>It is in humans, not rodents.</strong> Most microplastic toxicology
          before 2024 was either in vitro or in laboratory animals at unrealistic
          doses. This is real patients at real exposure levels.
        </li>
        <li>
          <strong>It links physical particles to clinical events.</strong> Not a
          biomarker, not an animal-model proxy, actual heart attacks, strokes,
          and deaths.
        </li>
        <li>
          <strong>It found a biological mechanism.</strong> Plaque containing
          plastic also showed elevated levels of the inflammatory markers IL-6,
          IL-18, and TNF-α, plus macrophage infiltration, a plausible chain from
          plastic to inflammation to plaque instability to clinical event.
        </li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Important limitations
      </h2>
      <p>
        The NEJM editorial accompanying the paper was unusually direct in its
        praise but also explicit about what this trial does <em>not</em> show:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Single-centre.</strong> All patients were treated at one Italian
          hospital. Replication in other populations is essential.
        </li>
        <li>
          <strong>No exposure measurement.</strong> The study measured plastic in
          plaque but did not measure each patient&apos;s historical plastic intake. We
          cannot say which lifestyle behaviours led to which plaque content.
        </li>
        <li>
          <strong>Association, not causation.</strong> Patients who accumulate plastic
          may also have other unmeasured risk factors. Randomising humans to plastic
          exposure is obviously impossible.
        </li>
        <li>
          <strong>Endarterectomy patients aren&apos;t representative</strong> of the general
          population, these were people already sick enough to need surgery.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How did the plastic get into their arteries?
      </h2>
      <p>
        The dominant polymers (polyethylene and PVC) match the materials we ingest
        and inhale most. Polyethylene is everywhere: plastic bags, milk jugs, water
        pipes, food packaging films. PVC is in pipes, food wrap, vinyl flooring,
        and some medical tubing. From gut and lung absorption, particles
        small enough to cross epithelial barriers reach the bloodstream and can
        embed in inflammatory tissue like atherosclerotic plaque, where they
        accumulate over years.
      </p>
      <p>
        See our explainer on{' '}
        <Link href="/blog/microplastics-in-human-blood" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in human blood
        </Link>{' '}
        for the upstream evidence on circulation.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What this means for everyday decisions
      </h2>
      <p>
        A 4.5× hazard ratio is large enough that it would change clinical guidelines
        if it were a drug. Until the result is replicated and quantified into a
        per-particle exposure relationship, no health authority will issue formal
        guidance. But the precautionary case for individual action is now stronger
        than at any prior point in the field.
      </p>
      <p>
        The exposure reductions that do the most, supported across the entire
        peer-reviewed literature, remain:
      </p>
      <ol className="list-decimal pl-6 space-y-2">
        <li>Filter your tap water (RO or carbon block) and stop drinking bottled water</li>
        <li>Replace plastic food storage and water bottles with glass or stainless steel</li>
        <li>Never heat food or water in plastic, microwaving releases millions of particles per container per cycle</li>
        <li>Choose natural fibres and HEPA-filter indoor air</li>
        <li>Reduce ultra-processed and heavily packaged food</li>
      </ol>
      <p>
        For a full action plan, see{' '}
        <Link href="/how-to-avoid-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          how to avoid microplastics
        </Link>{' '}
        and{' '}
        <Link href="/blog/best-plastic-free-food-storage" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          how to reduce kitchen microplastics
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Product packaging. PET, HDPE, PP, PS, PVC, multi-layer, glass, aluminum.</>,
          <>Container condition from photo, scratches, dents, fade.</>,
          <>Brand and product category, flags for known PFAS / BPA / fragranced lines.</>,
          <>Use-context flags, heat exposure, microwave, reuse cycles.</>,
          <>Cited published research, every score links the specific studies behind it.</>,
        ]}
      />

      <BlogAppCTA
        headline="Translate the research into 5-second shelf decisions"
        sub="Reading the studies is step one. Acting on them at the grocery store is step two. The MicroPlastics app scores each product 0–100 using research like this."
        buttonLabel="Get the MicroPlastics app"
      />

      <BlogFAQ
        items={[
          {
            question: 'What did the 2024 NEJM microplastics study find?',
            answer:
              'In 257 patients followed for 34 months after carotid plaque removal, those whose plaque contained polyethylene or PVC microplastics had a 4.53× higher risk of heart attack, stroke, or death from any cause, after adjusting for standard cardiovascular risk factors.',
          },
          {
            question: 'Does microplastics in arteries cause heart attacks?',
            answer:
              'The 2024 study shows a strong statistical association but cannot prove causation in a single trial. The 4.53× hazard ratio, supported by elevated inflammatory markers in plastic-positive plaque, makes a causal contribution biologically plausible and is shifting clinical opinion.',
          },
          {
            question: 'Which plastics were found in human arteries?',
            answer:
              'Polyethylene was detected in 58% of plaque samples and polyvinyl chloride (PVC) in approximately 12%. Both are common in food packaging, water pipes, plastic films, and disposable products.',
          },
          {
            question: 'Can plastic in arteries be removed?',
            answer:
              'There is no medical procedure to remove embedded microplastics from atherosclerotic plaque short of surgical endarterectomy. The only proven intervention is reducing ongoing exposure so future accumulation is slower.',
          },
          {
            question: 'How do microplastics get into arteries?',
            answer:
              'Particles small enough to cross the gut wall or pulmonary epithelium enter the bloodstream. They tend to accumulate in inflammatory tissue, including atherosclerotic plaque, where they may persist for years and amplify local inflammation.',
          },
          {
            question: 'What can I do to reduce my arterial microplastic exposure?',
            answer:
              'The four daily actions that matter most are: filter your tap water (RO or carbon block), eliminate bottled water and plastic food storage, never heat food in plastic, and use HEPA-filtered indoor air. Consistency over years matters more than perfection.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Marfella R, Prattichizzo F, Sardu C, et al.',
            year: 2024,
            title: 'Microplastics and nanoplastics in atheromas and cardiovascular events',
            publisher: 'New England Journal of Medicine 390:900-910',
            url: 'https://www.nejm.org/doi/full/10.1056/NEJMoa2309822',
          },
          {
            authors: 'Landrigan PJ.',
            year: 2024,
            title: 'Editorial: Microplastics, the Heart, and the Public&apos;s Health',
            publisher: 'New England Journal of Medicine',
            url: 'https://www.nejm.org/doi/full/10.1056/NEJMe2402524',
          },
          {
            authors: 'Leslie HA, van Velzen MJM, Brandsma SH, et al.',
            year: 2022,
            title: 'Discovery and quantification of plastic particle pollution in human blood',
            publisher: 'Environment International',
            url: 'https://www.sciencedirect.com/science/article/pii/S0160412022001258',
          },
          {
            authors: 'World Health Organization',
            year: 2022,
            title: 'Dietary and inhalation exposure to nano- and microplastic particles and potential implications for human health',
            publisher: 'WHO',
            url: 'https://www.who.int/publications/i/item/9789240054608',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
